"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { serializeProduct } from "@/lib/serialize-product";
import type { Product } from "@/types";

type WishlistState = {
  wishlistOwnerId: string | null;
  items: Product[];
  toggle: (product: Product) => void;
  add: (product: Product) => void;
  remove: (productId: string) => void;
  has: (productId: string) => boolean;
};

let syncUserId: string | null = null;

export function setWishlistSyncUser(userId: string | null) {
  syncUserId = userId;
}

async function syncAdd(productId: string) {
  if (!syncUserId) return;
  try {
    await fetch("/api/wishlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId })
    });
  } catch {
    // best-effort sync
  }
}

async function syncRemove(productId: string) {
  if (!syncUserId) return;
  try {
    await fetch("/api/wishlist", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId })
    });
  } catch {
    // ignore network errors
  }
}

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set, get) => ({
      wishlistOwnerId: null,
      items: [],
      toggle: (product) => {
        const exists = get().items.some((item) => item.id === product.id);
        if (exists) {
          set({ items: get().items.filter((item) => item.id !== product.id) });
          void syncRemove(product.id);
          return;
        }
        set({ items: [...get().items, product] });
        void syncAdd(product.id);
      },
      add: (product) => {
        const exists = get().items.some((item) => item.id === product.id);
        if (exists) return;
        set({ items: [...get().items, product] });
        void syncAdd(product.id);
      },
      remove: (productId) => {
        set({ items: get().items.filter((item) => item.id !== productId) });
        void syncRemove(productId);
      },
      has: (productId) => get().items.some((item) => item.id === productId)
    }),
    {
      name: "mova-ethiopia-wishlist",
      version: 2,
      migrate: () => ({
        wishlistOwnerId: null,
        items: [],
      }),
    }
  )
);

type ServerWishlistRow = { productId: string; product: Parameters<typeof serializeProduct>[0] };

/** Called on sign-in: sets the user's saved wishlist from the server as the sole source of truth. */
export async function hydrateWishlistFromServer(userId: string) {
  try {
    if (typeof window !== "undefined" && window.sessionStorage?.getItem("mova_new_registration") === "true") {
      useWishlistStore.setState({ items: [], wishlistOwnerId: userId });
      setWishlistSyncUser(userId);
      return;
    }

    const response = await fetch("/api/wishlist");
    if (!response.ok) {
      setWishlistSyncUser(userId);
      return;
    }

    const data = (await response.json()) as { items: ServerWishlistRow[] };
    const serverItems = data.items.map((row) => serializeProduct(row.product));

    useWishlistStore.setState({ items: serverItems, wishlistOwnerId: userId });
    setWishlistSyncUser(userId);
  } catch {
    setWishlistSyncUser(userId);
  }
}
